import { Component, OnInit } from '@angular/core';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { SpinnerService } from '../../shared/spinner/spinner.service';
import { KeywordsViewService } from './keywords-view.service';
import { MainViewService } from '../../main-view/main-view.service';
import { SharedService } from '../../shared/shared.service';
import { TranslatedNotificationService } from '../../shared/translation/translated-notification.service';

import {
  TranslationDto,
  CategoryBase,
  TranslationDetailsDto,
  EditTranslationDto,
  DescriptionDto,
  FilterableTranslation,
  Language,
  CategoryTranslationCollectionDto
} from '../../shared/model';
import { EditKeywordComponent } from './edit-keyword/edit-keyword.component';
import { EditCategoryComponent } from './edit-category/edit-category.component';

import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-keywords-view',
  templateUrl: './keywords-view.component.html',
  styleUrls: [
    './keywords-view.component.css',
    '../admin-grid.css',
    '../../shared/styles/small-navigation.css',
    '../../shared/styles/confirm-dialog.css']
})
export class KeywordsViewComponent implements OnInit {

  categoriesCollapsed = [];
  allCollapsed: boolean;
  allLanguages: Language[];
  translations: TranslationDto[];
  allCategoryTranslations: CategoryTranslationCollectionDto[];
  searchInput: string;
  categories: CategoryBase[];
  deleteOpened: boolean;
  keywordToDelete: string;
  descriptions: DescriptionDto[];

  private filteredKeywords: Array<FilterableTranslation> = [];
  private filterables: Array<FilterableTranslation> = [];
  private currentlyGeneratingDescriptionId: number;

  constructor(
    private keywordsViewService: KeywordsViewService,
    private spinnerService: SpinnerService,
    private notificationService: TranslatedNotificationService,
    private mainService: MainViewService,
    private sharedService: SharedService,
    private modalService: NgbModal) { }

  ngOnInit() {
    this.spinnerService.show('spinner');
    this.fetchData();
  }

  fetchData() {
    return forkJoin(
      this.keywordsViewService.getTranslations(),
      this.mainService.getAdminCategories(),
      this.keywordsViewService.getDescriptions(),
      this.keywordsViewService.getAllCategoryTranslations(),
      this.sharedService.getLanguages())
      .subscribe(([translations, categories, descriptions, categoryTranslations, languages]) => {
        this.translations = translations;
        this.categories = this.sharedService.translateCategories(categories);
        this.descriptions = descriptions;
        this.allCategoryTranslations = categoryTranslations;
        this.allLanguages = languages;
        this.createExpandedCategories();
        this.createFilterableTranslations();
        this.spinnerService.hide('spinner');
      },
      (err) => {
        this.spinnerService.hide('spinner');
        this.notificationService.showMsgError('error.loading_data', 'Error while loading data');
      });
  }

  fetchKeywords() {
    this.spinnerService.show('spinner');
    this.keywordsViewService.getTranslations()
      .subscribe(res => {
        this.translations = res;
        this.createExpandedCategories();
        this.createFilterableTranslations();
        this.spinnerService.hide('spinner');
      },
      () => {
        this.spinnerService.hide('spinner');
        this.notificationService.showMsgError('error.loading_keywords', 'Error while loading keywords');
      });
  }

  fetchCategories() {
    forkJoin(
      this.keywordsViewService.getAllCategoryTranslations(),
      this.mainService.getAdminCategories())
        .subscribe(([translations, categories]) => {
          this.sharedService.getCategoryTranslations()
            .subscribe(() => {
              this.categories = this.sharedService.translateCategories(categories);
              this.allCategoryTranslations = translations;
            });
        },
        (err) => {
          this.notificationService.showMsgError('error.loading_categories', 'Error while loading categories');
        });
  }

  fetchDescriptions() {
    this.keywordsViewService.getDescriptions()
      .subscribe(res => {
        this.descriptions = res;
      },
      (err) => {
        this.spinnerService.hide('spinner');
        this.notificationService.showMsgError('error.loading_descriptions', 'Error while loading descriptions');
      });
  }

  categoryCollapsed(categoryId) {
    if (this.categoriesCollapsed && this.categoriesCollapsed.length > 0) {
      const category = this.categoriesCollapsed.find(c => c.id === categoryId);
      return category ? category.collapsed : true;
    }
    return true;
  }

  createExpandedCategories() {
    this.categoriesCollapsed = [];
    this.translations.forEach(c => {
      this.categoriesCollapsed.push({
        id: c.CategoryId,
        collapsed: false
      });
    });
  }

  collapseCategory(elem) {
    const category = this.categoriesCollapsed.find(c => c.id === elem.srcElement.id);
    category.collapsed = !category.collapsed;
    this.setAllCollapsed();
  }

  setAllCollapsed() {
    const areAllCollapsed = (this.categoriesCollapsed.findIndex(c => c.collapsed === false) === -1);
    this.allCollapsed = areAllCollapsed;
  }

  collapseAll() {
    this.setCollapseForAllElements(true);
    this.setAllCollapsed();
  }

  expandAll() {
    this.setCollapseForAllElements(false);
    this.setAllCollapsed();
  }

  setCollapseForAllElements(collapse: boolean) {
    this.categoriesCollapsed.forEach(e => {
      e.collapsed = collapse;
    });
  }

  openFormModal() {
    const modalRef = this.modalService.open(EditKeywordComponent);
    modalRef.componentInstance.categories = this.categories;
    modalRef.componentInstance.allTranslations = this.getAllTranslations();
    modalRef.componentInstance.addingKeyword = true;
    modalRef.componentInstance.markAllAsDirty();

    modalRef.result.then((result) => {
      this.handleAddKeywordResult(result, false);
    }).catch(error => error);
  }

  generateKeyword(keyword: TranslationDetailsDto) {
    const translation = this.getTranslationDto(keyword, '');
    const modalRef = this.modalService.open(EditKeywordComponent);
    this.prepareModalRef(modalRef, translation, true);
    modalRef.componentInstance.markAllAsDirty();

    modalRef.result.then((result) => {
      this.handleAddKeywordResult(result, true);
    }).catch(error => error);
  }

  getTranslationDto(result: any, categoryId?: string): EditTranslationDto {
    return <EditTranslationDto>{
      CategoryId: categoryId === '' ? categoryId : result.Category.Id,
      TranslationDetails: <TranslationDetailsDto>{
        KeywordId: result.KeywordId ? result.KeywordId : '00000000-0000-0000-0000-000000000000',
        English: result.English ? result.English : '',
        French: result.French ? result.French : '',
        German: result.German ? result.German : '',
        Spanish: result.Spanish ? result.Spanish : '',
        Dutch: result.Dutch ? result.Dutch : '',
        Italian: result.Italian ? result.Italian : ''
      }
    };
  }

  deleteKeyword(keywordId: string) {
    this.spinnerService.show('spinner');
    this.keywordsViewService.deleteKeyword(keywordId).subscribe(() => {
    },
    (err) => {
      this.spinnerService.hide('spinner');
      this.notificationService.showMsgError('error.deleting_keyword', 'Error while deleting keyword');
    },
    () => {
      this.fetchKeywords();
    });
  }

  editKeyword(translationId: string): void {
    const translation = this.findTranslation(translationId);
    const modalRef = this.modalService.open(EditKeywordComponent);
    this.prepareModalRef(modalRef, translation, false);
    modalRef.componentInstance.markAllAsDirty();

    modalRef.result.then((result) => {
      this.handleEditKeywordResult(result);
    }).catch(error => error);
  }

  findTranslation(id: string): EditTranslationDto {
    const categoryWithTranslation = this.translations.find(c => c.Translations.findIndex(t => t.Id === id) >= 0);
    const translation = categoryWithTranslation.Translations.find(tr => tr.Id === id);

    return <EditTranslationDto>{
      CategoryId: categoryWithTranslation.CategoryId,
      TranslationDetails: translation
    };
  }

  deleteClick(keywordId: string) {
    this.keywordToDelete = keywordId;
    this.deleteOpened = true;
  }

  deleteClose() {
    this.deleteOpened = false;
    this.keywordToDelete = null;
  }

  deleteConfirmed() {
    this.deleteKeyword(this.keywordToDelete);
    this.deleteClose();
  }

  editCategory(categoryId: string): void {
    const categoryToEdit = this.categories.find(c => c.Id === categoryId);
    const modalRef = this.modalService.open(EditCategoryComponent);
    const translation = this.allCategoryTranslations.find(t => t.CategoryId === categoryId);
    modalRef.componentInstance.category = categoryToEdit;
    modalRef.componentInstance.categoryTranslations = translation ? translation.Translations : [];
    modalRef.componentInstance.languages = this.allLanguages;
    modalRef.componentInstance.setFormValues(categoryToEdit);

    modalRef.result.then((result) => {
      this.spinnerService.show('spinner');
      const category = this.getCategoryBase(result);
      const categoryTranslations = result;
      return forkJoin(
        this.keywordsViewService.editCategory(category),
        this.keywordsViewService.editCategoryTranslations(categoryTranslations)
      ).subscribe(() => {
        this.fetchCategories();
        this.spinnerService.hide('spinner');
      },
      (err) => {
        this.spinnerService.hide('spinner');
        this.notificationService.showMsgError('error.editing_category', 'Error while editing category');
      });
    }).catch(error => error);
  }

  getCategoryBase(result): CategoryBase {
    const englishTranslation = result.Translations.find(t => t.LanguageId === this.allLanguages.find(l => l.Name === 'English').Id);

    return <CategoryBase>{
      Id: result.CategoryId,
      Name: englishTranslation ? englishTranslation.Name : '',
      Description: englishTranslation ? englishTranslation.Description : ''
    };
  }

  getCategoryName(categoryId: string): string {
    return this.categories.find(c => c.Id === categoryId).Name;
  }

  currentDescription(descriptionId: number) {
    this.currentlyGeneratingDescriptionId = descriptionId;
  }

  verifyDescription(descriptionId) {
    this.spinnerService.show('spinner');
    this.keywordsViewService.setDescriptionVerified(descriptionId)
      .subscribe(() => {
        this.fetchDescriptions();
        this.spinnerService.hide('spinner');
      },
        (err) => {
          this.spinnerService.hide('spinner');
          this.notificationService
            .showMsgError('error.verifying_description', 'Error while setting description to verified');
        });
  }

  getAllTranslations(): Array<TranslationDetailsDto> {
    const translationsOnly = [];

    this.translations.forEach(c => {
      translationsOnly.push(c.Translations);
    });

    return [].concat.apply([], translationsOnly);
  }

  createFilterableTranslations() {
    if (this.translations && this.translations.length > 0) {
      this.translations.forEach(t => {
        if (t.Translations.length > 0) {
          t.Translations.forEach(tr => {
            const filterable = <FilterableTranslation>{};
            filterable.KeywordId = tr.KeywordId;
            filterable.Translations = [];
            Object.keys(tr).forEach(param => {
              if (!(param === 'KeywordId' || param === 'Id')) {
                filterable.Translations.push(tr[param]);
              }
            });
            this.filterables.push(filterable);
          });
        }
      });
    }
  }

  filter(input: string) {
    if (input === undefined || input === null || input.trim().length === 0) {
      this.filteredKeywords = [];
    }
    this.filteredKeywords = this.filterables.filter(
      (s) => s.Translations.findIndex(t => t.toLowerCase().indexOf(input.toLowerCase()) !== -1) !== -1
    );
    this.searchInput = input;
  }

  isHighlighted(keywordId: string) {
    if (this.filteredKeywords.length === 0 || this.searchInput === '') {
      return false;
    }
    return this.filteredKeywords.findIndex(fk => fk.KeywordId === keywordId) >= 0;
  }

  private handleAddKeywordResult(result: any, generatingKeyword: boolean) {
    this.spinnerService.show('spinner');
    const keyword = this.getTranslationDto(result);
    this.keywordsViewService.addKeyword(keyword)
      .subscribe(() => {
        if (generatingKeyword) {
          this.verifyDescription(this.currentlyGeneratingDescriptionId);
        }
      },
      (err) => {
        this.spinnerService.hide('spinner');
        this.notificationService.showMsgError('error.creating_keyword', 'Error while creating keyword');
      },
      () => {
        this.fetchKeywords();
      });
  }

  private handleEditKeywordResult(result: any) {
    this.spinnerService.show('spinner');
    const keyword = this.getTranslationDto(result);
    this.keywordsViewService.editKeyword(keyword)
      .subscribe(() => {},
        (err) => {
          this.spinnerService.hide('spinner');
          this.notificationService.showMsgError('error.editing_keyword', 'Error while editing keyword');
        },
        () => {
          this.fetchKeywords();
        });
  }

  private prepareModalRef(modalRef: any, translation: EditTranslationDto, generatingKeyword: boolean) {
    modalRef.componentInstance.categories = this.categories;
    modalRef.componentInstance.keyword = translation;
    modalRef.componentInstance.addingKeyword = generatingKeyword;
    modalRef.componentInstance.allTranslations = this.getAllTranslations();
    modalRef.componentInstance.setFormValues(translation);
  }
}
